function findByText(text, exact = false) {
	const els = document.querySelectorAll('span, p, label, h6, h5, h4, button, .MuiTypography-root')
	for (const el of els) {
		const content = el.textContent.trim().toLowerCase()
		if (exact ? content === text.toLowerCase() : content.includes(text.toLowerCase())) {
			return el
		}
	}
	return null
}

function findTableColumn(headerText) {
	const headers = document.querySelectorAll('th, [class*="MUIDataTableHeadCell"]')
	for (const h of headers) {
		if (h.textContent.trim().toLowerCase().includes(headerText.toLowerCase())) {
			return h
		}
	}
	return null
}

const ES = navigator.language?.startsWith('es')

export const TOUR_STEPS = [
	{
		type: 'intro',
		icon: 'clock-rotate-left',
		title: ES ? 'Bienvenido al Historial de Búsquedas' : 'Welcome to Search History',
		description: ES
			? 'La página de Historial muestra un registro de todas las búsquedas en Tiempo Real realizadas, tanto las manuales como las ejecutadas por informes o lotes. Cada fila representa una búsqueda con todos sus detalles, y puedes reabrir cualquier búsqueda directamente en Búsquedas en Tiempo Real.'
			: 'The Historical page shows a log of all Real-Time searches performed — both manual searches and those triggered by reports or batches. Each row represents one search with full details, and you can re-open any search directly in Real Time Searches.',
	},
	{
		type: 'field',
		findTarget: () => {
			const el = findByText('Historical List')
			if (el) {
				const paper = el.closest('.MuiPaper-root')
				if (paper) return paper
			}
			const cards = document.querySelectorAll('.MuiCard-root, .MuiPaper-root')
			for (const card of cards) {
				if (card.querySelector('table')) return card
			}
			return null
		},
		icon: 'table-cells',
		title: ES ? 'Lista del historial' : 'Historical List',
		description: ES
			? 'La tabla principal lista cada búsqueda con sus parámetros. Las filas incluyen la fecha de búsqueda, oficinas de recogida/devolución, webs consultadas, fechas de alquiler, duraciones y un enlace rápido para ver los resultados.'
			: 'The main table lists every search with its parameters. Rows include the search date, pickup/dropoff offices, websites queried, rental dates, rental durations, and a quick link to view results.',
	},
	{
		type: 'field',
		findTarget: () => findTableColumn('Date of Search'),
		icon: 'calendar-day',
		title: ES ? 'Fecha de búsqueda' : 'Date of Search',
		description: ES
			? 'Indica cuándo se ejecutó la búsqueda. Muestra la fecha exacta en formato legible (ej. "15 Mar 2025"). Las búsquedas se listan en orden cronológico inverso: las más recientes primero.'
			: 'When the search was executed. Shows the exact date in a readable format (e.g. "15 Mar 2025"). Searches are listed in reverse chronological order — newest first.',
	},
	{
		type: 'field',
		findTarget: () => findTableColumn('Office'),
		icon: 'city',
		title: ES ? 'Oficina (Recogida y Devolución)' : 'Office (Pickup & Dropoff)',
		description: ES
			? 'Muestra la oficina de recogida con la bandera del país y un icono de tipo (aeropuerto, centro ciudad, estación de tren). Si fue un alquiler de solo ida, la oficina de devolución aparece debajo con un estilo de icono diferente.'
			: 'Shows the pickup office with a country flag and type icon (airport, downtown, train station). If it was a one-way rental, the dropoff office appears below with a different icon style.',
		example: ES
			? 'Icono de avión = Aeropuerto | Icono de ciudad = Centro | Icono de tren = Estación'
			: 'Plane icon = Airport | City icon = Downtown | Train icon = Station',
	},
	{
		type: 'field',
		findTarget: () => findTableColumn('Sites'),
		icon: 'globe-pointer',
		title: ES ? 'Webs consultadas' : 'Websites Queried',
		description: ES
			? 'Lista todas las webs de alquiler y metabuscadores incluidos en esta búsqueda. Muestra los logotipos y banderas de cada sitio. Si se consultaron más de 10 sitios, un botón "+" abre un modal con la lista completa.'
			: 'Lists all rental websites and metasearch engines included in this search. Shows site logos and country flags. If more than 10 sites were queried, a "+" button opens a modal with the full list.',
	},
	{
		type: 'field',
		findTarget: () => findTableColumn('Date of Rental'),
		icon: 'calendar-days',
		title: ES ? 'Fecha de alquiler (DOR)' : 'Date of Rental (DOR)',
		description: ES
			? 'Las fechas de recogida que se buscaron. Si hay varias fechas, aparecen separadas por comas. Son las fechas futuras en las que los clientes recogerían el coche.'
			: 'The pickup dates that were searched. Multiple dates appear separated by commas. These are the future dates customers would pick up the car.',
		example: ES
			? '15 Mar 2025, 22 Mar 2025, 01 Abr 2025'
			: '15 Mar 2025, 22 Mar 2025, 01 Apr 2025',
	},
	{
		type: 'field',
		findTarget: () => findTableColumn('Length of Rental'),
		icon: 'calendar-clock',
		title: ES ? 'Duración del alquiler (LOR)' : 'Length of Rental (LOR)',
		description: ES
			? 'Las duraciones de alquiler en días incluidas en la búsqueda. Cada combinación de Fecha × LOR generó una tarea de búsqueda.'
			: 'The rental durations in days that were included in the search. Each combination of Date × LOR created one search task.',
		example: ES
			? '3, 5, 7 días'
			: '3, 5, 7 days',
	},
	{
		type: 'field',
		findTarget: () => findTableColumn('Real Time Searches'),
		icon: 'magnifying-glass-arrow-right',
		title: ES ? 'Ver en Tiempo Real' : 'View in Real Time',
		description: ES
			? 'Haz clic en el icono de búsqueda para reabrir los resultados completos de esa búsqueda en la página de Búsquedas en Tiempo Real. Se abre en una nueva pestaña con el ID de búsqueda original, para que puedas ver todos los resultados, el mapa de calor de precios y acceder a los Analizadores.'
			: 'Click the search icon to re-open the full results of that search in the Real Time Searches page. It opens in a new tab with the original search ID, so you can see all results, the price heat map, and access Analyzers.',
	},
	{
		type: 'field',
		findTarget: () => {
			const rows = document.querySelectorAll('table tbody tr')
			for (const row of rows) {
				const btn = row.querySelector('[title*="Real Time"]') || row.querySelector('[aria-label*="Real Time"]')
				if (btn) return btn
			}
			return null
		},
		skipIf: () => !document.querySelector('table tbody tr'),
		icon: 'magnifying-glass-arrow-right',
		title: ES ? 'Abrir búsqueda en Tiempo Real' : 'Open Search in Real Time',
		description: ES
			? 'El icono de lupa en cada fila abre esa búsqueda específica en la página de Búsquedas en Tiempo Real, en una nueva pestaña. Se cargan automáticamente todos los parámetros de esa ejecución: fechas de alquiler, oficinas, webs consultadas y duraciones. Es la forma más rápida de revisar un resultado concreto del historial sin tener que configurar los filtros de nuevo.'
			: 'The magnifying glass icon in each row opens that specific search in the Real Time Searches page, in a new tab. All parameters from that run are automatically loaded — rental dates, offices, websites queried, and durations. It\'s the fastest way to revisit a specific result from history without reconfiguring filters.',
	},
	{
		type: 'outro',
		icon: 'star',
		title: ES ? '¡Todo listo!' : "You're All Set!",
		description: ES
			? 'Explora tu historial de búsquedas y haz clic en el icono de búsqueda de cualquier fila para revisar sus resultados completos. Esta página también está enlazada desde Informes y Lotes: cuando un informe ejecuta búsquedas en tiempo real, también aparecen aquí.'
			: 'Browse your search history and click any row\'s search icon to revisit its full results. This page is also linked from Reports and Batches — when a report triggers real-time searches, they appear here too.',
	},
]
